import {FiCheckCircle, FiMinusCircle} from "react-icons/fi";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {Select} from "@/components/select/Select.tsx";
import {Table} from "@/components/table/Table.tsx";
import {Sorting, SortingDirection, TextAlignment} from "@/components/table/Table.types.ts";
import {AccountUserItems} from "@/lib/queries/accounts.ts";
import {Account, AccountItems, AccountUser, displayForUserAccessLevel, UserAccessLevel} from "@/lib/types/accounts.ts";
import {UserActions} from "@/pages/accounts/list/actions/UserActions.tsx";

type AccountSelectProps = {
  availableAccounts: Account[];
  selectedAccountID?: number;
  onChange: (accountId?: number) => void;
};

export const AccountSelect = ({availableAccounts, selectedAccountID, onChange}: AccountSelectProps) => {
  const options = availableAccounts.map((account) => ({value: account.id, label: account.name}));
  const value = options.filter((option) => option.value === selectedAccountID)[0] || null;
  return (
    <Select
      options={options}
      onChange={(selected) => onChange(selected?.value)}
      value={value}
      isClearable={true}
      placeholder="All Subaccounts"
    />
  );
};

type UsersTableProps = {
  users: AccountUserItems;
  accountAccounts: AccountItems;
  subaccountID?: number;
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  onSubaccountChange: (subaccount?: number) => void;
};

export const UsersTable = ({
  users,
  accountAccounts,
  subaccountID,
  sortColumn = "name",
  sortDirection,
  onSortingChange,
  onSubaccountChange,
}: UsersTableProps) => {
  const {user: currentUser} = useCurrentUserContext();
  return (
    <>
      {accountAccounts.items.length > 0 && (
        <div className="d-inline-flex pb-3 align-items-center">
          <span className="pe-3">Filter</span>
          <AccountSelect
            availableAccounts={accountAccounts.items}
            selectedAccountID={subaccountID}
            onChange={onSubaccountChange}
          />
        </div>
      )}
      <div>
        <Table
          rows={users.items}
          minWidth="800px"
          sorting={{column: sortColumn, direction: sortDirection}}
          onSortingChange={onSortingChange}
          specs={[
            {
              fieldName: "name",
              accessor: (obj: AccountUser) => `${obj.first_name} ${obj.last_name}`,
              fieldTitle: "Name",
              enableSorting: true,
            },
            {
              fieldName: "is_active",
              meta: {columnClassName: "justify-items-center", headerTextAlignment: TextAlignment.Center},
              accessor: (obj: AccountUser) =>
                obj.is_active ? (
                  <Icon icon={FiCheckCircle} className={"text-success"} />
                ) : (
                  <Icon icon={FiMinusCircle} className={"text-danger"} />
                ),
              fieldTitle: "Active",
              enableSorting: true,
            },
            {
              fieldName: "access_level",
              accessor: (obj: AccountUser) => displayForUserAccessLevel(obj),
              fieldTitle: "Access Level",
              enableSorting: true,
            },
            {
              hidden: !currentUser?.account?.has_can_purchase_feature,
              fieldName: "has_purchase_permission",
              meta: {columnClassName: "justify-items-center", headerTextAlignment: TextAlignment.Center},
              fieldTitle: "Can Purchase",
              enableSorting: true,
              accessor: (obj: AccountUser) =>
                obj.has_purchase_permission ? (
                  <Icon icon={FiCheckCircle} className={"text-success"} />
                ) : (
                  <Icon icon={FiMinusCircle} className={"text-danger"} />
                ),
            },
            {
              fieldName: "is_api_enabled",
              meta: {columnClassName: "justify-items-center", headerTextAlignment: TextAlignment.Center},
              accessor: (obj: AccountUser) =>
                obj.is_api_enabled ? (
                  <Icon icon={FiCheckCircle} className={"text-success"} />
                ) : (
                  <Icon icon={FiMinusCircle} className={"text-danger"} />
                ),
              fieldTitle: "API",
            },
            {
              fieldName: "is_mobile_enabled",
              meta: {columnClassName: "justify-items-center", headerTextAlignment: TextAlignment.Center},
              accessor: (obj: AccountUser) =>
                obj.is_mobile_enabled ? (
                  <Icon icon={FiCheckCircle} className={"text-success"} />
                ) : (
                  <Icon icon={FiMinusCircle} className={"text-danger"} />
                ),
              fieldTitle: "Mobile App",
            },
            {
              fieldName: "email",
              accessor: (obj: AccountUser) => {
                return (
                  <>
                    {obj.email}
                    {obj.username !== obj.email && (
                      <div>
                        <small className="text-muted">Username: {obj.username}</small>
                      </div>
                    )}
                    {obj.sso_user_bindings.map((binding, index) => (
                      <div key={index}>
                        <small className="text-muted">
                          {binding.type}: {binding.sso_username}
                        </small>
                      </div>
                    ))}
                  </>
                );
              },
              fieldTitle: "Email",
            },
            {
              fieldName: "must_two_factor",
              meta: {columnClassName: "justify-items-center", headerTextAlignment: TextAlignment.Center},
              accessor: (obj: AccountUser) =>
                obj.must_two_factor ? (
                  <Icon icon={FiCheckCircle} className={"text-success"} />
                ) : (
                  <Icon icon={FiMinusCircle} className={"text-danger"} />
                ),
              fieldTitle: "2FA Required",
              enableSorting: true,
            },
            {
              hidden: accountAccounts.total === 0,
              fieldName: "subaccounts",
              accessor: (obj: AccountUser) => {
                const subaccountIDs = accountAccounts.items.map((subaccount: Account) => subaccount.id);

                if (obj.access_level >= UserAccessLevel.ADMIN || obj.assigned_subaccounts.length === 0) {
                  return <div>All Subaccounts</div>;
                } else {
                  return obj.assigned_subaccounts.map((subaccount) => {
                    if (subaccountIDs.includes(subaccount.id)) {
                      return (
                        <div className="text-nowrap" key={subaccount.id}>
                          {subaccount.name}
                        </div>
                      );
                    }
                  });
                }
              },
              fieldTitle: "Subaccounts",
              meta: {headerTextAlignment: TextAlignment.Start},
            },
            {
              fieldName: "actions",
              meta: {columnClassName: "text-end"},
              accessor: (obj: AccountUser) => currentUser?.has_admin_access && <UserActions user={obj} />,
            },
          ].filter((row) => !(row?.hidden === true))}
          initialState={{columnPinning: {right: ["actions"]}}}
        />
      </div>
    </>
  );
};
